import { Link, createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { scheduleKey, scheduleQuery } from "@/lib/screen-queries";
import { useServerFn } from "@tanstack/react-start";
import { useAuth } from "@/lib/auth";
import { isGuard } from "@/lib/roles";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, CalendarClock, Plus, Shield } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { ShiftComplianceBadge, ShiftComplianceDialog } from "@/components/ShiftCompliance";
import { preflightShift, persistShiftCompliance, type ShiftRow } from "@/lib/compliance";
import {
  claimShift as claimShiftFn,
  createShift as createShiftFn,
  updateShift as updateShiftFn,
  deleteShift as deleteShiftFn,
  type ShiftConflict,
} from "@/lib/schedule.functions";
import { ShiftOfferBoard } from "@/components/schedule/ShiftOfferBoard";

export const Route = createFileRoute("/_authenticated/schedule")({
  // `?view=today` is the contextual "Today Shifts" mode used by Operations.
  // `?view=today` is the contextual "Today Shifts" mode; `?site=<id>` carries the
  // site scope a dashboard was working in (display filter only — RLS still governs
  // which shifts are readable).
  // `?offer=<id>` comes from a shift-offer notification: bring that offer into view.
  validateSearch: (search: Record<string, unknown>): { view?: "today"; site?: string; offer?: string } => ({
    view: search.view === "today" ? "today" : undefined,
    site: typeof search.site === "string" && search.site ? search.site : undefined,
    offer: typeof search.offer === "string" && search.offer ? search.offer : undefined,
  }),
  component: SchedulePage,
  head: () => ({ meta: [{ title: "Schedule — MissionReady" }] }),
});

type Shift = {
  id: string; organization_id: string; site_id: string | null; officer_id: string | null;
  starts_at: string; ends_at: string; role_label: string | null; status: string;
  compliance_status: string | null;
};
type Site = { id: string; name: string };
type Member = { user_id: string; profile?: { full_name: string | null; email: string | null } };

function SchedulePage() {
  const { view, site: siteScope, offer: focusOfferId } = Route.useSearch();
  const todayOnly = view === "today";
  const { currentOrg, user, currentRole } = useAuth();
  const guardView = isGuard(currentRole);
  // Shared with the prefetcher: warming /schedule fills this exact cache entry.
  const qc = useQueryClient();
  const orgId = currentOrg?.id ?? "";
  const { data: scheduleData } = useQuery(scheduleQuery(orgId));
  const shifts = (scheduleData?.shifts ?? []) as Shift[];
  const sites = (scheduleData?.sites ?? []) as Site[];
  const members = (scheduleData?.members ?? []) as Member[];
  const reloadSchedule = async () => {
    if (!orgId) return;
    await qc.invalidateQueries({ queryKey: scheduleKey(orgId) });
  };
  const [showForm, setShowForm] = useState(false);
  const [complianceShift, setComplianceShift] = useState<ShiftRow | null>(null);
  const [editingShift, setEditingShift] = useState<Shift | null>(null);
  const claim = useServerFn(claimShiftFn);
  const removeShift = useServerFn(deleteShiftFn);

  const deleteShift = async (s: Shift) => {
    if (!confirm("Delete this shift? This cannot be undone.")) return;
    try {
      await removeShift({ data: { shiftId: s.id } });
    } catch (e: any) { toast.error(e?.message ?? "Failed to delete shift"); return; }
    qc.setQueryData(scheduleKey(orgId), (prev: any) =>
      prev ? { ...prev, shifts: (prev.shifts ?? []).filter((x: Shift) => x.id !== s.id) } : prev,
    );
    toast.success("Shift deleted");
  };

  const claimShift = async (s: Shift) => {
    if (!user) return;
    try {
      const ev = await preflightShift(s as unknown as ShiftRow);
      if (ev.status === "blocked" && ev.enforcement_mode === "strict") {
        toast.error(`Blocked: ${ev.outcomes.find((o) => o.enforcement === "block" && o.status !== "ok")?.message ?? "missing required credentials"}`);
        setComplianceShift(s as unknown as ShiftRow);
        return;
      }
      if (ev.status === "warning") {
        toast.warning("Compliance warning — assignment allowed");
      }
    } catch (e: any) {
      console.warn("preflight failed", e);
    }
    try {
      const res = await claim({ data: { shiftId: s.id } });
      if (!res.ok) {
        const c = res.conflicts[0];
        toast.error(
          `Scheduling conflict: you already have a shift ${format(new Date(c.starts_at), "MMM d, HH:mm")}–${format(new Date(c.ends_at), "HH:mm")}. Resolve it first.`,
        );
        return;
      }
    } catch (e: any) { toast.error(e?.message ?? "Failed"); return; }
    toast.success("Shift claimed");
    try {
      await persistShiftCompliance(s.id);
    } catch (e) {
      console.error("[compliance] persist after claim failed", { shift_id: s.id, error: e });
      toast.warning("Shift claimed, but compliance snapshot failed to refresh");
    }
  };

  const canManage = ["org_owner", "admin", "supervisor", "dispatcher"].includes(currentRole ?? "");

  // Realtime keeps the cached schedule fresh in the background.
  useEffect(() => {
    if (!orgId) return;
    const ch = supabase.channel(`shifts:${orgId}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "shifts", filter: `organization_id=eq.${orgId}` },
        () => { void reloadSchedule(); },
      )
      .subscribe();
    return () => { supabase.removeChannel(ch); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  const memberName = (uid: string | null) => {
    if (!uid) return "Open";
    const m = members.find((x) => x.user_id === uid);
    return m?.profile?.full_name || m?.profile?.email || uid.slice(0, 8);
  };
  const siteName = (id: string | null) => sites.find((s) => s.id === id)?.name ?? "—";

  // Today = shifts overlapping the local calendar day.
  const dayStart = new Date(); dayStart.setHours(0, 0, 0, 0);
  const dayEnd = new Date(dayStart.getTime() + 24 * 60 * 60 * 1000);
  const scopedShifts = siteScope ? shifts.filter((s) => s.site_id === siteScope) : shifts;
  const visibleShifts = todayOnly
    ? scopedShifts.filter((s) => new Date(s.starts_at) < dayEnd && new Date(s.ends_at) >= dayStart)
    : scopedShifts;

  return (
    <div className="px-4 py-6 md:px-8 md:py-8">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CalendarClock className="h-5 w-5 text-primary" />
          <h1 className="font-display text-2xl font-bold md:text-3xl">
            {todayOnly ? "Today Shifts" : guardView ? "My Shifts" : "Schedule"}
          </h1>
        </div>
        {canManage && <Button onClick={() => setShowForm(!showForm)}><Plus className="mr-1 h-4 w-4" /> New shift</Button>}
      </div>
      <p className="text-sm text-muted-foreground">
        {todayOnly
          ? `Shifts covering ${format(dayStart, "EEEE, MMM d")}.`
          : guardView
            ? "Shifts assigned to you."
            : "Plan shifts, post open shifts, and let officers claim them."}
      </p>
      {todayOnly && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Link
            to="/operations"
            className="inline-flex min-h-[44px] items-center gap-2 rounded-lg px-2 text-sm font-semibold text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" /> Operations
          </Link>
          <Link
            to="/schedule"
            className="inline-flex min-h-[44px] items-center rounded-lg border border-border px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground"
          >
            Full schedule
          </Link>
        </div>
      )}

      {(showForm || editingShift) && currentOrg && user && (
        <ShiftForm
          key={editingShift?.id ?? "new"}
          orgId={currentOrg.id}
          userId={user.id}
          sites={sites}
          members={members}
          shift={editingShift}
          onDone={() => { setShowForm(false); setEditingShift(null); }}
        />
      )}

      {currentOrg && (
        <ShiftOfferBoard
          organizationId={currentOrg.id}
          currentRole={currentRole ?? null}
          userId={user?.id ?? null}
          sites={sites}
          focusOfferId={focusOfferId}
        />
      )}

      <div
        className="mt-6 overflow-x-auto overflow-y-hidden rounded-xl border border-border bg-card"
        tabIndex={0}
        role="group"
        aria-label="Schedule timeline (scrolls horizontally)"
      >
        <table className="w-full min-w-[34rem] text-sm">

          <thead className="bg-muted/40 text-xs uppercase text-muted-foreground">
            <tr>
              <th className="px-4 py-2 text-left">Start</th>
              <th className="px-4 py-2 text-left">End</th>
              <th className="px-4 py-2 text-left">Site</th>
              <th className="px-4 py-2 text-left">Officer</th>
              <th className="px-4 py-2 text-left">Role</th>
              <th className="px-4 py-2 text-left">Status</th>
              <th className="px-4 py-2 text-left">Compliance</th>
              <th className="px-4 py-2 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {visibleShifts.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">
                  {todayOnly ? "No shifts scheduled today." : "No shifts scheduled."}
                </td>
              </tr>
            )}
            {visibleShifts.map((s) => (
              <tr key={s.id} className="border-t border-border">
                <td className="px-4 py-2">{format(new Date(s.starts_at), "MMM d, HH:mm")}</td>
                <td className="px-4 py-2">{format(new Date(s.ends_at), "MMM d, HH:mm")}</td>
                <td className="px-4 py-2">{siteName(s.site_id)}</td>
                <td className="px-4 py-2">{memberName(s.officer_id)}</td>
                <td className="px-4 py-2">{s.role_label ?? "—"}</td>
                <td className="px-4 py-2"><span className="rounded-full border border-border px-2 py-0.5 text-xs">{s.status}</span></td>
                <td className="px-4 py-2">
                  <button className="inline-flex items-center gap-1" onClick={() => setComplianceShift(s as unknown as ShiftRow)}>
                    <ShiftComplianceBadge status={s.compliance_status} />
                  </button>
                </td>
                <td className="px-4 py-2 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <button className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1" onClick={() => setComplianceShift(s as unknown as ShiftRow)}>
                      <Shield className="h-3 w-3" /> Compliance
                    </button>
                    {!s.officer_id && user && (
                      <button className="text-xs text-primary hover:underline" onClick={() => claimShift(s)}>Claim</button>
                    )}
                    {canManage && (
                      <>
                        <button
                          className="text-xs text-muted-foreground hover:text-foreground"
                          onClick={() => { setShowForm(false); setEditingShift(s); }}
                        >
                          Edit
                        </button>
                        <button
                          className="text-xs text-destructive hover:underline"
                          onClick={() => deleteShift(s)}
                        >
                          Delete
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ShiftComplianceDialog open={!!complianceShift} onOpenChange={(v) => !v && setComplianceShift(null)} shift={complianceShift} />
    </div>
  );
}

const toLocalInput = (iso: string) => {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

function ShiftForm({ orgId, sites, members, shift, onDone }:
  { orgId: string; userId: string; sites: Site[]; members: Member[]; shift?: Shift | null; onDone: () => void }) {
  const now = new Date(); now.setMinutes(0, 0, 0);
  const startDefault = toLocalInput(new Date(now.getTime() + 60 * 60 * 1000).toISOString());
  const endDefault = toLocalInput(new Date(now.getTime() + 9 * 60 * 60 * 1000).toISOString());
  const [starts, setStarts] = useState(shift ? toLocalInput(shift.starts_at) : startDefault);
  const [ends, setEnds] = useState(shift ? toLocalInput(shift.ends_at) : endDefault);
  const [siteId, setSiteId] = useState(shift?.site_id ?? "");
  const [officerId, setOfficerId] = useState(shift?.officer_id ?? "");
  const [roleLabel, setRoleLabel] = useState(shift?.role_label ?? "");
  const create = useServerFn(createShiftFn);
  const update = useServerFn(updateShiftFn);
  const [conflicts, setConflicts] = useState<ShiftConflict[]>([]);
  const [saving, setSaving] = useState(false);
  const siteLabel = (id: string | null) => sites.find((s) => s.id === id)?.name ?? "No site";

  return (
    <div className="mt-4 rounded-xl border border-border bg-card p-5">
      <div className="grid gap-3 md:grid-cols-3">
        <div><Label>Starts</Label><Input aria-label="Starts" type="datetime-local" value={starts} onChange={(e) => setStarts(e.target.value)} /></div>
        <div><Label>Ends</Label><Input aria-label="Ends" type="datetime-local" value={ends} onChange={(e) => setEnds(e.target.value)} /></div>
        <div><Label>Role label</Label><Input aria-label="Role label" value={roleLabel} onChange={(e) => setRoleLabel(e.target.value)} placeholder="e.g. Patrol officer" /></div>
        <div>
          <Label>Site</Label>
          <Select value={siteId || "__none"} onValueChange={(v) => setSiteId(v === "__none" ? "" : v)}>
            <SelectTrigger aria-label="Site"><SelectValue placeholder="Select site" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none">No site</SelectItem>
              {sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Officer (leave empty for open shift)</Label>
          <Select value={officerId || "__none"} onValueChange={(v) => setOfficerId(v === "__none" ? "" : v)}>
            <SelectTrigger aria-label="Officer (leave empty for open shift)"><SelectValue placeholder="Open shift" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none">Open shift</SelectItem>
              {members.map((m) => (
                <SelectItem key={m.user_id} value={m.user_id}>
                  {m.profile?.full_name || m.profile?.email || m.user_id.slice(0, 8)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      {conflicts.length > 0 && (
        <div
          role="alert"
          className="mt-4 rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm"
        >
          <p className="font-semibold text-destructive">
            Scheduling conflict — this officer is already booked
          </p>
          <p className="mt-1 text-muted-foreground">
            Change the times or the officer, or edit the conflicting shift below, then save again.
          </p>
          <ul className="mt-2 space-y-1">
            {conflicts.map((c) => (
              <li key={c.id}>
                {format(new Date(c.starts_at), "MMM d, HH:mm")} – {format(new Date(c.ends_at), "MMM d, HH:mm")}
                {" · "}{siteLabel(c.site_id)}{c.role_label ? ` · ${c.role_label}` : ""}{" · "}{c.status}
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="mt-4 flex gap-2">
        <Button disabled={saving} onClick={async () => {
          let targetId: string | null = shift?.id ?? null;
          if (new Date(ends) <= new Date(starts)) {
            toast.error("Shift must end after it starts");
            return;
          }
          const payload = {
            startsAt: new Date(starts).toISOString(),
            endsAt: new Date(ends).toISOString(),
            siteId: siteId || null,
            officerId: officerId || null,
            roleLabel: roleLabel || null,
          };
          setSaving(true);
          try {
            if (shift) {
              const res = await update({ data: { shiftId: shift.id, ...payload } });
              if (!res.ok) { setConflicts(res.conflicts); toast.error("Overlapping shift — resolve the conflict first"); return; }
            } else {
              const res = await create({ data: { orgId, ...payload } });
              if (!res.id) { setConflicts(res.conflicts); toast.error("Overlapping shift — resolve the conflict first"); return; }
              targetId = res.id;
            }
          } catch (e: any) { toast.error(e?.message ?? "Failed"); return; }
          finally { setSaving(false); }
          setConflicts([]);
          toast.success(shift ? "Shift updated" : "Shift created");
          if (officerId && targetId) {
            try {
              await persistShiftCompliance(targetId);
            } catch (e) {
              console.error("[compliance] persist after save failed", { shift_id: targetId, error: e });
              toast.warning("Shift saved, but compliance snapshot failed to refresh");
            }
          }
          onDone();
        }}>{shift ? "Save changes" : "Create shift"}</Button>
        <Button variant="outline" onClick={onDone}>Cancel</Button>
      </div>
    </div>
  );
}
